import { useState, useEffect, useRef, useCallback } from 'react';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Grid,
  Tab,
  Tabs,
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import useCustomer from 'src/api/accounts-receivable/customers/useCustomer';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import Icon from '@mui/material/Icon';
import SalesOrderModal from 'src/pages/authenticated/core/sales-orders/components/SalesOrderModal';
import SalesOrderDraftModal from 'src/pages/authenticated/core/sales-order-drafts/components/sales-order-draft-modal/SalesOrderDraftModal';
import AliasesBox from './AliasesBox';
import EditableField from '../common/EditableField';
import useUpdateCustomerIsaId from 'src/api/accounts-receivable/customers/useUpdateCustomerIsaId';
import useUpdateCustomerProduct from 'src/api/accounts-receivable/customers/useUpdateCustomerProduct';
import useDeleteCustomerProduct from 'src/api/accounts-receivable/customers/useDeleteCustomerProduct';
import { useToast } from 'src/context/ToastContext';
import Watchers from '../watchers/Watchers';

interface Props {
  customerId: string;
  onClose: () => void;
}

// Tab mapping for URL-friendly names
const tabNames = ['orders', 'products', 'communication', 'contacts'];
const getTabIndex = (tabName: string): number => {
  const index = tabNames.indexOf(tabName);
  return index === -1 ? 0 : index;
};

const CustomerModal: React.FC<Props> = ({ customerId, onClose }) => {
  const [
    selectedStagedCustomerPurchaseOrderIdModal,
    setSelectedStagedCustomerPurchaseOrderIdModal,
  ] = useState<string>();
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<string>();
  const navigate = useNavigate({ from: '/customers' });
  const search = useSearch({ from: '/authenticated/customers' });
  const activeTabName =
    ((search as Record<string, unknown>).tab as string) || 'orders';
  const activeTab = getTabIndex(activeTabName);
  const setActiveTab = useCallback(
    (tab: string) => {
      navigate({ to: '.', search: { customerId, tab } });
    },
    [navigate, customerId],
  );
  const { showToast } = useToast();

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{
    id: string;
    name: string;
    customerDescription: string | null;
    customerSku: string | null;
    customerUpc: string | null;
  } | null>(null);
  // Handle tab change and update URL
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(tabNames[newValue]);
  };
  const { data, isLoading, error, refetch } = useCustomer(customerId, {
    include: [
      'products',
      'purchaseOrders',
      'aiGeneratedEmails',
      'contacts',
      'isaId',
      'locations',
    ],
  });
  const updateIsaId = useUpdateCustomerIsaId(customerId, () => {
    refetch();
  });

  // State to track which customer product is being updated
  const [updatingCustomerProductId, setUpdatingCustomerProductId] = useState<
    string | null
  >(null);

  // State for optimistic updates
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<
      string,
      Partial<{
        customerDescription: string | null;
        customerSku: string | null;
        customerUpc: string | null;
      }>
    >
  >({});

  // State for optimistic deletions
  const [optimisticallyDeleted, setOptimisticallyDeleted] = useState<
    Set<string>
  >(new Set());

  // Update customer product hook
  const updateCustomerProduct = useUpdateCustomerProduct(() => {
    setUpdatingCustomerProductId(null);
    refetch();
  });

  // Delete customer product hook
  const deleteCustomerProduct = useDeleteCustomerProduct(() => {
    setDeleteConfirmOpen(false);
    setProductToDelete(null);
    refetch();
  });

  // Clear optimistic updates when actual data matches optimistic updates
  useEffect(() => {
    if (!data?.customer?.products) return;

    // Clear optimistic updates when data matches
    setOptimisticUpdates((prev) => {
      const newUpdates = { ...prev };
      let hasChanges = false;

      // Check each product to see if the actual data now matches our optimistic updates
      data.customer.products?.forEach((product) => {
        const optimisticProduct = prev[product.id];
        if (optimisticProduct) {
          let shouldClear = true;

          // Check if actual data matches optimistic data for each field
          Object.entries(optimisticProduct).forEach(
            ([field, optimisticValue]) => {
              const actualValue = product[field as keyof typeof product];
              if (actualValue !== optimisticValue) {
                shouldClear = false;
              }
            },
          );

          if (shouldClear) {
            delete newUpdates[product.id];
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newUpdates : prev;
    });

    // Clear optimistic deletions when fresh data arrives (confirms deletions)
    setOptimisticallyDeleted((prev) => {
      if (prev.size === 0) return prev;

      const actualProductIds = new Set(
        data?.customer.products?.map((p) => p.id) || [],
      );
      const stillDeleted = new Set(
        [...prev].filter((id) => !actualProductIds.has(id)),
      );

      // If any optimistically deleted items reappear in actual data, remove from optimistic deletions
      return stillDeleted.size === prev.size ? prev : stillDeleted;
    });
  }, [data?.customer?.products]);

  // Helper function to update customer product fields
  const handleUpdateCustomerProduct = useCallback(
    (
      customerProductId: string,
      field: 'customerDescription' | 'customerSku' | 'customerUpc',
      value: string,
    ) => {
      const finalValue = value || null;

      // Apply optimistic update immediately
      setOptimisticUpdates((prev) => ({
        ...prev,
        [customerProductId]: {
          ...prev[customerProductId],
          [field]: finalValue,
        },
      }));

      setUpdatingCustomerProductId(customerProductId);

      updateCustomerProduct.mutate(
        {
          customerProductId,
          [field]: finalValue,
        },
        {
          onError: () => {
            // Revert optimistic update on error
            setOptimisticUpdates((prev) => {
              const newUpdates = { ...prev };
              if (newUpdates[customerProductId]) {
                delete newUpdates[customerProductId][field];
                if (Object.keys(newUpdates[customerProductId]).length === 0) {
                  delete newUpdates[customerProductId];
                }
              }
              return newUpdates;
            });

            showToast({
              message: 'Failed to update customer product.',
              title: 'Error',
              color: 'error',
              icon: 'warning',
            });
            setUpdatingCustomerProductId(null);
          },
        },
      );
    },
    [updateCustomerProduct, showToast],
  );

  // Helper function to get display value with optimistic updates
  const getDisplayValue = useCallback(
    (
      customerProductId: string,
      field: 'customerDescription' | 'customerSku' | 'customerUpc',
      originalValue: string | null | undefined,
    ) => {
      const optimisticValue = optimisticUpdates[customerProductId]?.[field];
      return optimisticValue !== undefined ? optimisticValue : originalValue;
    },
    [optimisticUpdates],
  );

  // Handle delete confirmation
  const handleDeleteClick = useCallback(
    (customerProduct: {
      id: string;
      customerDescription?: string | null;
      customerSku?: string | null;
      customerUpc?: string | null;
      productVariant: {
        originMetadata?: {
          NAMEINVC?: string | null;
        } | null;
      };
    }) => {
      setProductToDelete({
        id: customerProduct.id,
        name:
          (customerProduct.productVariant.originMetadata?.NAMEINVC as string) ||
          'Unknown Product',
        customerDescription: customerProduct.customerDescription || null,
        customerSku: customerProduct.customerSku || null,
        customerUpc: customerProduct.customerUpc || null,
      });
      setDeleteConfirmOpen(true);
    },
    [],
  );

  // Handle confirmed delete
  const handleConfirmDelete = useCallback(() => {
    if (!productToDelete) return;

    // Optimistically delete the product immediately
    setOptimisticallyDeleted((prev) => new Set([...prev, productToDelete.id]));

    // Close the modal immediately
    setDeleteConfirmOpen(false);
    setProductToDelete(null);

    deleteCustomerProduct.mutate(
      { customerProductId: productToDelete.id },
      {
        onError: () => {
          // Revert optimistic deletion on error
          setOptimisticallyDeleted((prev) => {
            const newSet = new Set(prev);
            newSet.delete(productToDelete.id);
            return newSet;
          });

          showToast({
            message: 'Failed to delete customer product.',
            title: 'Error',
            color: 'error',
            icon: 'warning',
          });
        },
      },
    );
  }, [productToDelete, deleteCustomerProduct, showToast]);

  if (isLoading) {
    return (
      <Modal
        open
        title="Customer Details"
        closeable
        onCloseButton={{ text: 'Close', onClick: onClose }}
        hideCloseButton
      >
        <MDBox display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </MDBox>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        open
        title="Customer Details"
        closeable
        onCloseButton={{ text: 'Close', onClick: onClose }}
        hideCloseButton
      >
        <ErrorMessage error={error} />
      </Modal>
    );
  }

  const customer = data?.customer;

  if (!customer) {
    return null;
  }

  const googleMapsLink =
    customer.latitude && customer.longitude
      ? `https://www.google.com/maps?q=${customer.latitude},${customer.longitude}`
      : null;

  return (
    <Modal
      maxWidth="xl"
      open
      title={`Customer: ${customer.name}`}
      closeable
      onCloseButton={{ text: 'Close', onClick: onClose }}
      hideCloseButton
      disableContentOverflow
    >
      <MDBox sx={{ height: '100%', overflow: 'hidden' }}>
        <Grid container spacing={3} sx={{ height: '100%', overflow: 'hidden' }}>
          {/* Left Column */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Card
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <MDBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <MDBox p={3} sx={{ flexShrink: 0 }}>
                  <MDTypography variant="h6" fontWeight="medium" mb={3}>
                    Basic Information
                  </MDTypography>
                  <InfoRow
                    label="Name"
                    value={customer.erpDisplayName ?? customer.name}
                  />
                  <AliasesBox
                    customerId={customerId}
                    values={customer.aliases}
                    onUpdate={() => {
                      refetch();
                    }}
                  />
                  <EditableField
                    label="ISA ID"
                    value={customer.isaId ?? null}
                    onUpdate={async (value) => {
                      updateIsaId.mutate(
                        { isaId: value },
                        {
                          onSuccess: () => {
                            refetch();
                          },
                          onError: () => {
                            showToast({
                              message: 'Failed to update value.',
                              title: 'Error',
                              color: 'error',
                              icon: 'warning',
                            });
                          },
                        },
                      );
                    }}
                    isLoading={updateIsaId.isLoading}
                    placeholder="Enter ISA ID"
                  />
                  {/* <InfoRow label="Email Domain" value={customer.emailDomain} />
                <InfoRow label="Phone Number" value={customer.phoneNumber} />
                <InfoRow
                  label="Contact Person"
                  value={customer.contactPerson}
                /> */}
                  <InfoRow
                    label="Assigned/Followers"
                    value={
                      <Watchers
                        watchableEntityType="customer"
                        watchableId={customerId}
                      />
                    }
                  />
                </MDBox>
              </MDBox>
            </Card>

            <Card
              sx={{
                mt: 3,
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <MDBox p={3} sx={{ flex: 1, overflowY: 'auto' }}>
                <MDTypography variant="h6" fontWeight="medium" mb={3}>
                  Address & Payment
                </MDTypography>

                <InfoRowWithLink
                  label="Primary Address"
                  link={googleMapsLink ?? undefined}
                  linkIcon="map"
                  value={
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#333',
                        lineHeight: 1.3,
                        whiteSpace: 'pre-line',
                        marginTop: 1,
                      }}
                    >
                      {customer.address || 'No address on file'}
                    </div>
                  }
                />

                <InfoRow label="Payment Terms" value={customer.paymentTerms} />

                <InfoRow
                  label="Credit Limit"
                  value={formatCreditLimit(
                    customer.creditLimit,
                    customer.creditLimitCurrency ?? null,
                  )}
                />

                <LocationsSection locations={customer.locations ?? []} />
              </MDBox>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Card
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <MDBox
                p={3}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{ mb: 3, flexShrink: 0 }}
                >
                  <Tab label="Orders" />
                  <Tab label="Products" />
                  <Tab label="Communication" />
                  <Tab label="Contacts" />
                </Tabs>

                <MDBox
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    pr: 1,
                    minHeight: 0,
                  }}
                >
                  {/* Orders Tab */}
                  {activeTab === 0 && (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Recent Orders
                      </MDTypography>

                      <TableContainer>
                        <Table>
                          <TableHead sx={{ display: 'table-header-group' }}>
                            <TableRow>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Order ID
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Date
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Total Amount
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                ></MDTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customer.purchaseOrders?.map((order) => (
                              <TableRow key={order.id}>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    #{order.purchaseOrderNumber}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {order.createdAt
                                      ? new Date(
                                          order.createdAt,
                                        ).toLocaleDateString()
                                      : '-'}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {order.totalAmount
                                      ? `$${Number(order.totalAmount).toFixed(
                                          2,
                                        )}`
                                      : '-'}
                                  </MDTypography>
                                </TableCell>

                                <TableCell>
                                  <MDBox
                                    display="flex"
                                    justifyContent="center"
                                    gap={1}
                                    onClick={(e: MouseEvent<HTMLDivElement>) =>
                                      e.stopPropagation()
                                    }
                                  >
                                    {!order.stagedCustomerPurchaseOrderId ? null : (
                                      <Tooltip title="See original purchase order">
                                        <Icon
                                          sx={{
                                            cursor: 'pointer',
                                            color: 'info.main',
                                            '&:hover': {
                                              color: 'info.dark',
                                            },
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedStagedCustomerPurchaseOrderIdModal(
                                              order.stagedCustomerPurchaseOrderId ??
                                                undefined,
                                            );
                                          }}
                                        >
                                          visibility
                                        </Icon>
                                      </Tooltip>
                                    )}

                                    {!order.salesOrder ? null : (
                                      <Tooltip title="See sales order">
                                        <Icon
                                          sx={{
                                            cursor: 'pointer',
                                            color: 'info.main',
                                            '&:hover': {
                                              color: 'info.dark',
                                            },
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedSalesOrderId(
                                              order.salesOrder?.id ?? undefined,
                                            );
                                          }}
                                        >
                                          receipt_long
                                        </Icon>
                                      </Tooltip>
                                    )}
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            ))}
                            {(!customer.purchaseOrders ||
                              customer.purchaseOrders.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <MDBox textAlign="center" py={2}>
                                    <MDTypography variant="body2" color="text">
                                      No orders found for this customer
                                    </MDTypography>
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </MDBox>
                  )}

                  {/* Products Tab */}
                  {activeTab === 1 && (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Customer Products
                      </MDTypography>

                      <MDBox
                        mb={2}
                        p={1.5}
                        borderRadius="lg"
                        sx={{
                          backgroundColor: 'grey.100',
                          border: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ fontSize: 18 }} color="info">
                            info
                          </Icon>
                          <MDTypography variant="caption" color="text">
                            Products as requested by this customer (using their
                            SKUs and terminology), mapped to corresponding items
                            in your inventory system
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDBox>
                        <Table>
                          <TableHead sx={{ display: 'table-header-group' }}>
                            <TableRow>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Variant
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Customer Product Name/Description
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Customer SKU
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Customer UPC
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                ></MDTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customer.products
                              ?.filter(
                                (product) =>
                                  !optimisticallyDeleted.has(product.id),
                              )
                              ?.map((customerProduct) => (
                                <TableRow
                                  key={customerProduct.id}
                                  sx={{
                                    '&:hover .delete-action': {
                                      opacity: 1,
                                    },
                                    '& .delete-action': {
                                      opacity: 0,
                                      transition: 'opacity 0.2s ease',
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <MDTypography
                                      variant="caption"
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {
                                        customerProduct.productVariant
                                          .originMetadata?.NAMEINVC as string
                                      }
                                    </MDTypography>
                                  </TableCell>

                                  <TableCell>
                                    <EditableProductCell
                                      value={getDisplayValue(
                                        customerProduct.id,
                                        'customerDescription',
                                        customerProduct.customerDescription,
                                      )}
                                      onSave={(value) => {
                                        handleUpdateCustomerProduct(
                                          customerProduct.id,
                                          'customerDescription',
                                          value,
                                        );
                                      }}
                                      placeholder="-"
                                      isLoading={
                                        updatingCustomerProductId ===
                                          customerProduct.id &&
                                        updateCustomerProduct.isLoading
                                      }
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <EditableProductCell
                                      value={getDisplayValue(
                                        customerProduct.id,
                                        'customerSku',
                                        customerProduct.customerSku,
                                      )}
                                      onSave={(value) => {
                                        handleUpdateCustomerProduct(
                                          customerProduct.id,
                                          'customerSku',
                                          value,
                                        );
                                      }}
                                      placeholder="-"
                                      isLoading={
                                        updatingCustomerProductId ===
                                          customerProduct.id &&
                                        updateCustomerProduct.isLoading
                                      }
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <EditableProductCell
                                      value={getDisplayValue(
                                        customerProduct.id,
                                        'customerUpc',
                                        customerProduct.customerUpc,
                                      )}
                                      onSave={(value) => {
                                        handleUpdateCustomerProduct(
                                          customerProduct.id,
                                          'customerUpc',
                                          value,
                                        );
                                      }}
                                      placeholder="-"
                                      isLoading={
                                        updatingCustomerProductId ===
                                          customerProduct.id &&
                                        updateCustomerProduct.isLoading
                                      }
                                    />
                                  </TableCell>

                                  <TableCell>
                                    <MDBox
                                      display="flex"
                                      justifyContent="center"
                                    >
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(
                                          e: MouseEvent<HTMLElement>,
                                        ) => {
                                          e.stopPropagation();

                                          handleDeleteClick(customerProduct);
                                        }}
                                        className="delete-action"
                                        sx={{
                                          p: 0.5,
                                          '&:hover': {
                                            backgroundColor:
                                              'rgba(211, 47, 47, 0.04)',
                                          },
                                        }}
                                      >
                                        <DeleteOutlineIcon
                                          sx={{ transform: 'scale(0.8)' }}
                                        />
                                      </IconButton>
                                    </MDBox>
                                  </TableCell>
                                </TableRow>
                              ))}
                            {(!customer.products ||
                              customer.products.length === 0 ||
                              customer.products.every((product) =>
                                optimisticallyDeleted.has(product.id),
                              )) && (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <MDBox textAlign="center" py={2}>
                                    <MDTypography variant="body2" color="text">
                                      No products linked to this customer
                                    </MDTypography>
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </MDBox>
                    </MDBox>
                  )}

                  {/* Communication Tab */}
                  {activeTab === 2 && (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Communication History
                      </MDTypography>

                      <MDBox>
                        <TableContainer>
                          <Table>
                            <TableHead sx={{ display: 'table-header-group' }}>
                              <TableRow>
                                <TableCell>
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    fontWeight="medium"
                                  >
                                    Date
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    fontWeight="medium"
                                  >
                                    Sender
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    fontWeight="medium"
                                  >
                                    Details
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {customer.aiGeneratedEmails?.map((email) => (
                                <TableRow key={email.id}>
                                  <TableCell>
                                    <MDTypography variant="body2">
                                      {email.sentAt
                                        ? new Date(
                                            email.sentAt,
                                          ).toLocaleDateString()
                                        : '-'}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant="body2">
                                      {email.sender.name}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDBox
                                      display="flex"
                                      flexDirection="column"
                                      p={2}
                                      sx={{
                                        backgroundColor: 'grey.100',
                                        border: '1px solid',
                                        borderColor: 'grey.300',
                                        borderRadius: 1,
                                        '& a': { color: 'info.main' },
                                      }}
                                    >
                                      {/* Email Header */}
                                      <MDBox
                                        pb={1.5}
                                        mb={1.5}
                                        sx={{
                                          borderBottom: '1px solid',
                                          borderColor: 'grey.300',
                                        }}
                                      >
                                        <MDBox mb={1}>
                                          <MDTypography
                                            variant="body2"
                                            color="text"
                                            fontWeight="regular"
                                          >
                                            To:{' '}
                                            <MDTypography
                                              component="span"
                                              variant="body2"
                                              fontWeight="medium"
                                            >
                                              {email.to}
                                            </MDTypography>
                                          </MDTypography>
                                        </MDBox>
                                        <MDBox>
                                          <MDTypography
                                            variant="body2"
                                            color="text"
                                            fontWeight="regular"
                                          >
                                            Subject:{' '}
                                            <MDTypography
                                              component="span"
                                              variant="body2"
                                              fontWeight="medium"
                                            >
                                              {email.subject}
                                            </MDTypography>
                                          </MDTypography>
                                        </MDBox>
                                      </MDBox>

                                      {/* Email Body */}
                                      <MDBox>
                                        <MDTypography
                                          variant="body2"
                                          color="text"
                                          sx={{
                                            whiteSpace: 'pre-line',
                                            lineHeight: 1.6,
                                          }}
                                        >
                                          {email.body}
                                        </MDTypography>
                                      </MDBox>
                                    </MDBox>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {(!customer.aiGeneratedEmails ||
                                customer.aiGeneratedEmails.length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <MDBox textAlign="center" py={2}>
                                      <MDTypography
                                        variant="body2"
                                        color="text"
                                      >
                                        No email communications found
                                      </MDTypography>
                                    </MDBox>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    </MDBox>
                  )}

                  {/* Contacts Tab */}
                  {activeTab === 3 && (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium" mb={3}>
                        Contact List
                      </MDTypography>

                      <TableContainer>
                        <Table>
                          <TableHead sx={{ display: 'table-header-group' }}>
                            <TableRow>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Name
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Email
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Phone
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                >
                                  Role
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customer.contacts?.map((contact) => (
                              <TableRow key={contact.id}>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {contact.name}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {contact.email}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {contact.phone || '-'}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="body2">
                                    {contact.title || '-'}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            ))}
                            {(!customer.contacts ||
                              customer.contacts.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <MDBox textAlign="center" py={2}>
                                    <MDTypography variant="body2" color="text">
                                      No contacts found for this customer
                                    </MDTypography>
                                  </MDBox>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {selectedSalesOrderId ? (
        <SalesOrderModal
          hideSuggestedActions
          salesOrderId={selectedSalesOrderId}
          onClose={() => {
            setSelectedSalesOrderId(undefined);
          }}
        />
      ) : null}
      {selectedStagedCustomerPurchaseOrderIdModal && (
        <SalesOrderDraftModal
          salesOrderDraftId={selectedStagedCustomerPurchaseOrderIdModal}
          onClose={() =>
            setSelectedStagedCustomerPurchaseOrderIdModal(undefined)
          }
          editable={false}
          isLoading={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && productToDelete && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setProductToDelete(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
            },
          }}
        >
          <DialogTitle>
            <MDBox display="flex" alignItems="center">
              <Icon color="warning" sx={{ mr: 1, fontSize: '1.5rem' }}>
                warning
              </Icon>
              <MDTypography variant="h5" fontWeight="medium">
                Delete Customer Product
              </MDTypography>
            </MDBox>
          </DialogTitle>

          <DialogContent>
            <MDTypography variant="body2" color="text.secondary" mb={3}>
              Are you sure you want to delete this customer product mapping?
            </MDTypography>

            <MDBox
              p={2}
              sx={{
                backgroundColor: 'grey.100',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Product Variant:
                </MDTypography>
                <MDTypography variant="body2" ml={1}>
                  {productToDelete.name}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Customer Description:
                </MDTypography>
                <MDTypography
                  variant="body2"
                  ml={1}
                  color={
                    productToDelete.customerDescription
                      ? 'text'
                      : 'text.secondary'
                  }
                >
                  {productToDelete.customerDescription || '-'}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Customer SKU:
                </MDTypography>
                <MDTypography
                  variant="body2"
                  ml={1}
                  color={
                    productToDelete.customerSku ? 'text' : 'text.secondary'
                  }
                >
                  {productToDelete.customerSku || '-'}
                </MDTypography>
              </MDBox>

              <MDBox>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Customer UPC:
                </MDTypography>
                <MDTypography
                  variant="body2"
                  ml={1}
                  color={
                    productToDelete.customerUpc ? 'text' : 'text.secondary'
                  }
                >
                  {productToDelete.customerUpc || '-'}
                </MDTypography>
              </MDBox>
            </MDBox>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <MDButton
              variant="text"
              color="secondary"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setProductToDelete(null);
              }}
              disabled={deleteCustomerProduct.isLoading}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteCustomerProduct.isLoading}
              startIcon={
                deleteCustomerProduct.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {deleteCustomerProduct.isLoading ? 'Deleting...' : 'Delete'}
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
    </Modal>
  );
};

// Helper component for displaying info rows
const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <MDBox mb={2}>
    <MDTypography variant="caption" color="text" fontWeight="bold">
      {label}
    </MDTypography>
    <MDTypography variant="body2">{value || '-'}</MDTypography>
  </MDBox>
);

// Helper component for displaying info rows
const InfoRowWithLink: React.FC<{
  label: string;
  value?: React.ReactNode;
  link?: string;
  linkIcon?: string;
}> = ({ label, value, link, linkIcon }) => (
  <MDBox mb={2}>
    <MDTypography variant="caption" color="text" fontWeight="bold">
      {label}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer">
          <Icon
            sx={{ ml: '5px', verticalAlign: 'middle' }}
            color="info"
            fontSize="inherit"
          >
            {linkIcon}
          </Icon>
        </a>
      )}
    </MDTypography>
    <MDTypography variant="body2">{value || '-'}</MDTypography>
  </MDBox>
);

// Helper function for credit limit display
const formatCreditLimit = (value: unknown, currency: string | null) => {
  // Check if value is missing or invalid
  if (
    value === null ||
    value === undefined ||
    isNaN(Number(value)) ||
    Number(value) === 0
  ) {
    return (
      <MDBox display="flex" alignItems="center">
        <Icon sx={{ mr: '5px' }} color="warning" fontSize="small">
          info
        </Icon>
        <MDTypography variant="caption" color="warning">
          No credit limit
        </MDTypography>
      </MDBox>
    );
  }

  if (!currency) {
    return (
      <MDBox display="flex" alignItems="center">
        <Icon sx={{ mr: '5px' }} color="error" fontSize="small">
          error
        </Icon>
        <MDTypography variant="caption" color="error">
          Missing currency
        </MDTypography>
      </MDBox>
    );
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

  return formatted;
};

// Editable cell component for customer product fields
const EditableProductCell: React.FC<{
  value: string | null | undefined;
  onSave: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
}> = ({ value, onSave, placeholder = '', isLoading = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edit value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use requestAnimationFrame for more reliable focusing
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      });
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== (value || '')) {
      onSave(trimmedValue);
    }
    setIsEditing(false);
  }, [editValue, value, onSave]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      handleSave();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  if (isEditing) {
    return (
      <MDBox onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
        <TextField
          ref={inputRef}
          size="small"
          value={editValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEditValue(e.target.value)
          }
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          placeholder={placeholder}
          fullWidth
          autoFocus
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              padding: '4px 8px',
            },
          }}
        />
      </MDBox>
    );
  }

  return (
    <MDBox
      sx={{
        cursor: isLoading ? 'wait' : 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '24px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isLoading
            ? 'transparent'
            : 'rgba(25, 118, 210, 0.08)',
          border: isLoading
            ? '1px solid transparent'
            : '1px solid rgba(25, 118, 210, 0.3)',
        },
        border: isLoading
          ? '1px solid rgba(25, 118, 210, 0.5)'
          : '1px solid transparent',
        backgroundColor: isLoading ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        opacity: isLoading ? 0.7 : 1,
        ...(isLoading && {
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
            '50%': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderColor: 'rgba(25, 118, 210, 0.7)',
            },
            '100%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
          },
        }),
      }}
      onClick={isLoading ? undefined : handleClick}
    >
      <MDTypography variant="caption" sx={{ fontSize: '0.75rem' }}>
        {value || (
          <span style={{ color: '#999', fontStyle: 'italic' }}>
            {placeholder || 'Click to edit'}
          </span>
        )}
      </MDTypography>
    </MDBox>
  );
};

// Helper component for locations section
const LocationsSection: React.FC<{
  locations?: Array<{
    id: string;
    name: string | null;
    dunsNumber?: string | null;
    address: string | null;
    longitude: number | null;
    latitude: number | null;
    disabled?: boolean;
  }>;
}> = ({ locations }) => (
  <InfoRow
    label="Locations"
    value={
      !locations || locations.length === 0 ? (
        'No locations found'
      ) : (
        <MDBox component="div" mt={1} ml={2}>
          {locations.map((location, index) => (
            <div
              key={location.id}
              style={{
                marginBottom: index < locations.length - 1 ? '10px' : '0',
                paddingBottom: index < locations.length - 1 ? '8px' : '0',
                borderBottom:
                  index < locations.length - 1 ? '1px solid #f0f0f0' : 'none',
                opacity: location.disabled ? 0.5 : 1,
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 400,
                  color: location.disabled ? '#999' : '#333',
                  marginBottom: '2px',
                }}
              >
                {location.name}
                {location.disabled && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: '#dc2626',
                      backgroundColor: '#fef2f2',
                      padding: '1px 4px',
                      borderRadius: '2px',
                      marginLeft: '6px',
                    }}
                  >
                    Disabled
                  </span>
                )}
                {location.dunsNumber && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: '#999',
                      backgroundColor: '#f5f5f5',
                      padding: '1px 4px',
                      borderRadius: '2px',
                      marginLeft: '6px',
                    }}
                  >
                    #{location.dunsNumber}
                  </span>
                )}
                {location.longitude && location.latitude && (
                  <a
                    href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon
                      sx={{ ml: '5px', verticalAlign: 'middle' }}
                      color="info"
                      fontSize="inherit"
                    >
                      map
                    </Icon>
                  </a>
                )}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: location.disabled ? '#999' : '#666',
                  lineHeight: 1.3,
                }}
              >
                {location.address}
              </div>
            </div>
          ))}
        </MDBox>
      )
    }
  />
);

export default CustomerModal;
